import {
  ASSETS_URL_FORMATTER_TOKEN,
  AutoHeightDecorator,
  Color4,
  ComplexRenderConfig,
  Container,
  DimensionSource,
  DimensionSourceScale,
  EffectType,
  IAssetsUrlFormatter,
  RenderEffect,
  SceneObject,
  TtfRenderComponent,
  TtfTextSceneObject,
} from '@cgs/wge';

interface TranslationData {
  scenes: {
    id: string;
    items: TranslationItem[];
  }[];
}

interface GlowStrokeConfigJSON {
  enabled?: boolean;
  color?: string; // hex or rgba string
  width?: number;
  softness?: number;
  opacity?: number;
}

interface ShadowConfigJSON {
  enabled?: boolean;
  color?: string;
  offsetX?: number;
  offsetY?: number;
  softness?: number;
  opacity?: number;
  strokeWidth?: number;
}

interface GradientStopConfigJSON {
  offset?: number;
  color?: string;
}

interface GradientFillConfigJSON {
  enabled?: boolean;
  type?: 'linear';
  angle?: number;
  offset?: number; // -1..1
  stops?: GradientStopConfigJSON[];
}

interface ComplexEffectConfigJSON {
  id: string;
  type: 'outerGlow' | 'innerGlow' | 'gradient' | 'shadow' | 'fill';
  enabled?: boolean;
  order: number;
  config: GlowStrokeConfigJSON | GradientFillConfigJSON | ShadowConfigJSON | { color?: string };
}

interface ComplexEffectsConfigJSON {
  effects: ComplexEffectConfigJSON[];
  letterSpacing?: number;
  lineSpacing?: number;
  canvasScaleX?: number;
  canvasScaleY?: number;
}

interface RenderConfigJSON {
  fillColor?: string;
  fillGradient?: GradientFillConfigJSON;
  outerGlow?: GlowStrokeConfigJSON;
  innerGlow?: GlowStrokeConfigJSON;
  shadow?: ShadowConfigJSON;
  letterSpacing?: number;
  lineSpacing?: number;
  canvasScaleX?: number;
  canvasScaleY?: number;
  // Complex effects system
  complexEffects?: ComplexEffectsConfigJSON;
  // Autoscale configuration
  dimensionSource?: string; // 'Frame' | 'ProportionalSize' | 'Size'
  dimensionSourceScale?: string; // 'DownOnly' | 'UpOnly' | 'Both'
  // Canvas padding (separate for each side)
  paddingTop?: number;
  paddingRight?: number;
  paddingBottom?: number;
  paddingLeft?: number;
}

interface TranslationItem {
  id: string;
  text: string;
  lineSpacing?: number;
  fontSize?: number;
  autoHeight?: boolean;
  shiftSiblings?: boolean;
  DimensionSource?: DimensionSource;
  DimensionSourceScale?: DimensionSourceScale;
  FontSize?: number;
  render?: RenderConfigJSON;
}

export const LOCALIZATION_MANAGER_TOKEN = Symbol('LOCALIZATION_MANAGER_TOKEN');

export class LocalizationManager {
  private readonly translations: Map<string, TranslationItem> = new Map();
  private readonly translationsScoped: Map<string, TranslationItem> = new Map(); // key: `${sceneId}:${id}`
  private readonly assetsUrlFormatter: IAssetsUrlFormatter | null;
  private readonly folderPath = 'translates';

  private initPromise: Promise<void> | null = null;

  constructor(
    container: Container,
    private readonly locale: string
  ) {
    this.assetsUrlFormatter = container.resolve<IAssetsUrlFormatter>(ASSETS_URL_FORMATTER_TOKEN, {
      optional: true,
    });
  }

  public get lang(): string {
    return this.locale;
  }

  public init(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.loadTranslations().then(() => {
        SceneObject.initialized
          .filter((scene) => scene instanceof TtfTextSceneObject)
          .on((scene) => this.onTtfInitialize(scene as TtfTextSceneObject));
      });
    }

    return this.initPromise;
  }

  public get(key: string, fallback: string = ''): string {
    return this.translations.get(key)?.text || fallback;
  }

  public getAsync(key: string): Promise<string> {
    return Promise.resolve(this.get(key, ''));
  }

  public getFontSize(key: string): number | null {
    return this.translations.get(key)?.fontSize || null;
  }

  public applyToNode(ttf: TtfTextSceneObject): void {
    this.onTtfInitialize(ttf);
  }

  private onTtfInitialize(ttf: TtfTextSceneObject): void {
    if (!ttf || !ttf.id) {
      return;
    }

    ttf.id = ttf.id.replaceAll('\r', '').replaceAll('\n', '');

    const sceneUrl = ttf.sceneUrl;
    const sceneId = this.normalizeSceneUrlToSceneId(sceneUrl);

    // Prefer scoped key `${sceneId}:${id}`, fallback to legacy `id`
    const scopedKey = `${sceneId}:${ttf.id}`;
    const item = this.translationsScoped.get(scopedKey) ?? this.translations.get(ttf.id);

    if (item !== undefined) {
      ttf.text = item.text || ttf.text;
      ttf.textRenderParams.lang = this.locale;

      if (item.DimensionSource) {
        ttf.textRenderParams.dimensionSource = item.DimensionSource;
      }

      if (item.DimensionSourceScale) {
        ttf.textRenderParams.dimensionSourceScale = item.DimensionSourceScale;
      }

      if (typeof item.lineSpacing === 'number' && Number.isFinite(item.lineSpacing)) {
        ttf.lineSpacing = item.lineSpacing;
      }

      if (item.fontSize) {
        ttf.fontSize = item.fontSize;
      }

      if (ttf.forceFontSize) {
        ttf.fontSize = ttf.forceFontSize;
      }

      // Attach render component if render config present
      const renderComponent = this.buildRenderComponent(item.render);
      if (renderComponent) {
        ttf.setRenderComponent(renderComponent);
      }

      if (item.autoHeight) {
        const fixLangs = ['th', 'vi'];

        const autoHeight = new AutoHeightDecorator({
          shiftSiblings: item.shiftSiblings ?? true,
          fontSizeFix: fixLangs.includes(this.locale) && item.autoHeight ? 5 : 0,
        });
        autoHeight.apply(ttf);
      }
    } else {
      // console.warn(
      //   `LocalizationManager: Translation key not found: '${ttf.id}' (scene: '${sceneId}', url: '${sceneUrl}').`
      // );
    }
  }

  private async loadTranslations(): Promise<void> {
    const baseUrl = this.assetsUrlFormatter?.format(this.folderPath) || this.folderPath;
    const defaultUrl = `${baseUrl}/localization.default.json`;
    const localeUrl = `${baseUrl}/localization.${this.locale.toLowerCase()}.json`;

    const defaultData = await this.loadDefaultTranslations(defaultUrl);
    let localeData: TranslationData | null = null;
    try {
      const response = await fetch(localeUrl);
      if (response.ok) {
        localeData = await response.json();
      } else {
        // console.warn(
        //   `LocalizationManager: Locale file for '${this.locale}' not found. Using defaults.`
        // );
      }
    } catch (error) {
      console.warn(
        `LocalizationManager: Error loading locale '${this.locale}', using defaults.`,
        error
      );
    }

    const merged = this.mergeTranslations(defaultData, localeData);
    if (merged) {
      this.populateTranslationsMap(merged);
    }
  }

  private async loadDefaultTranslations(defaultUrl: string): Promise<TranslationData | null> {
    try {
      const response = await fetch(defaultUrl);
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error(
        `LocalizationManager: FATAL: Could not load default translations from ${defaultUrl}.`,
        error
      );
    }
    return null;
  }

  private mergeTranslations(
    base: TranslationData | null,
    override: TranslationData | null
  ): TranslationData | null {
    if (!base && !override) return null;
    if (!base) return override;
    if (!override) return base;

    const scenesMap = new Map<string, Map<string, TranslationItem>>();

    const ingest = (data: TranslationData) => {
      for (const scene of data.scenes) {
        if (!scenesMap.has(scene.id)) {
          scenesMap.set(scene.id, new Map());
        }
        const m = scenesMap.get(scene.id)!;
        for (const item of scene.items) {
          const prev = m.get(item.id);
          m.set(item.id, prev ? this.deepMergeItem(prev, item) : item);
        }
      }
    };

    ingest(base);
    ingest(override);

    const result: TranslationData = { scenes: [] } as any;
    for (const [sceneId, itemsMap] of scenesMap) {
      result.scenes.push({ id: sceneId, items: Array.from(itemsMap.values()) });
    }
    return result;
  }

  private deepMergeItem(a: TranslationItem, b: TranslationItem): TranslationItem {
    const out: TranslationItem = { ...a, ...b };
    if (a.render || b.render) {
      out.render = {
        ...a.render,
        ...b.render,
        outerGlow: { ...(a.render?.outerGlow ?? {}), ...(b.render?.outerGlow ?? {}) },
        innerGlow: { ...(a.render?.innerGlow ?? {}), ...(b.render?.innerGlow ?? {}) },
        shadow: { ...(a.render?.shadow ?? {}), ...(b.render?.shadow ?? {}) },
        // If complexEffects is present in override, use it (override takes priority)
        complexEffects: b.render?.complexEffects ?? a.render?.complexEffects,
      };
    }
    return out;
  }

  private buildRenderComponent(render?: RenderConfigJSON): TtfRenderComponent | null {
    if (!render) {
      return null;
    }

    const cfg: any = {};

    // Parse complexEffects if present (takes priority over legacy single effects)
    if (render.complexEffects) {
      cfg.complexEffects = this.parseComplexEffects(render.complexEffects);
    } else {
      // Legacy single effect parsing
      if (render.fillColor) cfg.fillColor = this.parseColor(render.fillColor);
      if (render.fillGradient) {
        cfg.fillGradient = this.parseGradient(render.fillGradient);
      }
      if (render.outerGlow) {
        cfg.outerGlow = this.parseGlow(render.outerGlow);
      }
      if (render.innerGlow) {
        cfg.innerGlow = this.parseGlow(render.innerGlow);
      }
      if (render.shadow) {
        cfg.shadow = this.parseShadow(render.shadow);
      }
      if (typeof render.letterSpacing === 'number') cfg.letterSpacing = render.letterSpacing;
      if (typeof render.lineSpacing === 'number') cfg.lineSpacing = render.lineSpacing;
      if (typeof render.canvasScaleX === 'number') cfg.canvasScaleX = render.canvasScaleX;
      if (typeof render.canvasScaleY === 'number') cfg.canvasScaleY = render.canvasScaleY;
    }

    // Autoscale configuration
    if (render.dimensionSource) cfg.dimensionSource = render.dimensionSource;
    if (render.dimensionSourceScale) cfg.dimensionSourceScale = render.dimensionSourceScale;

    // Canvas padding (separate for each side)
    if (typeof render.paddingTop === 'number') cfg.paddingTop = render.paddingTop;
    if (typeof render.paddingRight === 'number') cfg.paddingRight = render.paddingRight;
    if (typeof render.paddingBottom === 'number') cfg.paddingBottom = render.paddingBottom;
    if (typeof render.paddingLeft === 'number') cfg.paddingLeft = render.paddingLeft;

    // If nothing meaningful present, skip
    if (
      !cfg.fillColor &&
      !cfg.outerGlow &&
      !cfg.innerGlow &&
      !cfg.shadow &&
      !cfg.complexEffects &&
      cfg.letterSpacing === undefined &&
      cfg.lineSpacing === undefined &&
      cfg.canvasScaleX === undefined &&
      cfg.canvasScaleY === undefined &&
      !cfg.dimensionSource &&
      !cfg.dimensionSourceScale &&
      cfg.paddingTop === undefined &&
      cfg.paddingRight === undefined &&
      cfg.paddingBottom === undefined &&
      cfg.paddingLeft === undefined
    ) {
      return null;
    }

    return new TtfRenderComponent(cfg);
  }

  private parseComplexEffects(complexEffects: ComplexEffectsConfigJSON): ComplexRenderConfig {
    const effects: RenderEffect[] = [];

    // Sort effects by order
    const sortedEffects = [...complexEffects.effects].sort((a, b) => a.order - b.order);

    for (const effectJson of sortedEffects) {
      // Skip disabled effects
      if (effectJson.enabled === false) {
        continue;
      }

      let effect: RenderEffect | null = null;

      switch (effectJson.type) {
        case 'outerGlow':
        case 'innerGlow': {
          const glowConfig = effectJson.config as GlowStrokeConfigJSON;
          effect = {
            id: effectJson.id,
            type: effectJson.type === 'outerGlow' ? EffectType.OuterGlow : EffectType.InnerGlow,
            enabled: effectJson.enabled ?? true,
            order: effectJson.order,
            config: this.parseGlow(glowConfig),
          } as RenderEffect;
          break;
        }

        case 'gradient': {
          const gradientConfig = effectJson.config as GradientFillConfigJSON;
          effect = {
            id: effectJson.id,
            type: EffectType.Gradient,
            enabled: effectJson.enabled ?? true,
            order: effectJson.order,
            config: this.parseGradient(gradientConfig),
          } as RenderEffect;
          break;
        }

        case 'shadow': {
          const shadowConfig = effectJson.config as ShadowConfigJSON;
          effect = {
            id: effectJson.id,
            type: EffectType.Shadow,
            enabled: effectJson.enabled ?? true,
            order: effectJson.order,
            config: this.parseShadow(shadowConfig),
          } as RenderEffect;
          break;
        }

        case 'fill': {
          const fillConfig = effectJson as { color?: string };
          if (fillConfig.color) {
            effect = {
              id: effectJson.id,
              type: EffectType.Fill,
              enabled: effectJson.enabled ?? true,
              order: effectJson.order,
              color: this.parseColor(fillConfig.color),
            } as RenderEffect;
          }
          break;
        }
      }

      if (effect) {
        effects.push(effect);
      }
    }

    const result: ComplexRenderConfig = {
      effects,
    };

    if (typeof complexEffects.letterSpacing === 'number') {
      result.letterSpacing = complexEffects.letterSpacing;
    }
    if (typeof complexEffects.lineSpacing === 'number') {
      result.lineSpacing = complexEffects.lineSpacing;
    }
    if (typeof complexEffects.canvasScaleX === 'number') {
      result.canvasScaleX = complexEffects.canvasScaleX;
    }
    if (typeof complexEffects.canvasScaleY === 'number') {
      result.canvasScaleY = complexEffects.canvasScaleY;
    }

    return result;
  }

  private parseGlow(g: GlowStrokeConfigJSON) {
    return {
      enabled: g.enabled !== false,
      color: g.color ? this.parseColor(g.color) : undefined,
      width: g.width,
      softness: g.softness,
      opacity: g.opacity,
    };
  }

  private parseShadow(s: ShadowConfigJSON) {
    return {
      enabled: s.enabled !== false,
      color: s.color ? this.parseColor(s.color) : undefined,
      offsetX: s.offsetX,
      offsetY: s.offsetY,
      softness: s.softness,
      opacity: s.opacity,
      strokeWidth: s.strokeWidth,
    };
  }

  private parseColor(input: string): Color4 {
    // Supports #RRGGBB, RRGGBB, rgba(r,g,b,a)
    let hex = input.trim();
    if (hex.length === 6 && !hex.startsWith('#')) {
      hex = '#' + hex;
    }
    if (hex.startsWith('#') && hex.length === 7) {
      const r = parseInt(hex.substring(1, 3), 16) / 255;
      const g = parseInt(hex.substring(3, 5), 16) / 255;
      const b = parseInt(hex.substring(5, 7), 16) / 255;
      return new Color4(r, g, b, 1);
    }
    const rgba = /rgba?\(([^)]+)\)/.exec(hex);
    if (rgba) {
      const parts = rgba[1].split(',').map((p) => parseFloat(p.trim()));
      const [r, g, b, a] = [
        (parts[0] ?? 255) / 255,
        (parts[1] ?? 255) / 255,
        (parts[2] ?? 255) / 255,
        parts[3] ?? 1,
      ];
      return new Color4(r, g, b, a);
    }
    // Fallback to white
    return new Color4(1, 1, 1, 1);
  }

  private parseGradient(g: GradientFillConfigJSON) {
    return {
      enabled: g.enabled !== false,
      type: 'linear' as const,
      angle: g.angle,
      offset: g.offset,
      stops: (g.stops || [])
        .filter((s) => s && (s.color || s.offset !== undefined))
        .map((s) => ({
          offset: s.offset ?? 0,
          color: s.color ? this.parseColor(s.color) : new Color4(1, 1, 1, 1),
        })),
    };
  }
  private populateTranslationsMap(data: TranslationData): void {
    this.translations.clear();
    this.translationsScoped.clear();
    for (const scene of data.scenes) {
      for (const item of scene.items) {
        if (this.translations.has(item.id)) {
          console.warn(
            `LocalizationManager: Duplicate translation key '${item.id}' found in scene '${scene.id}'. Overwriting previous value.`
          );
        }
        item.fontSize = item.FontSize;
        this.translations.set(item.id, item);

        const scopedKey = `${scene.id}:${item.id}`;
        if (this.translationsScoped.has(scopedKey)) {
          console.warn(
            `LocalizationManager: Duplicate scoped key '${scopedKey}'. Overwriting previous value.`
          );
        }
        this.translationsScoped.set(scopedKey, item);
      }
    }
  }

  private normalizeSceneUrlToSceneId(sceneUrl: string | undefined): string {
    if (!sceneUrl) return '';
    // Convert resource id like 'bottom/sceneGameRules' -> 'bottom_sceneGameRules_object'
    const base = sceneUrl.replace(/\/+/, '/').replaceAll('/', '_');
    return `${base}_object`;
  }
}
